import { useState, type ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { api, fehlertext } from '../api'
import { useLaden } from '../hooks'
import Datenpruefung, { zaehlerText } from '../komponenten/Datenpruefung'
import FehlendeArtikel from '../komponenten/FehlendeArtikel'
import Meldung from '../komponenten/Meldung'
import OffenesAusDemAssistenten from '../komponenten/OffenesAusDemAssistenten'
import SelectLineWarnungen from '../komponenten/SelectLineWarnungen'
import UebergangeneRueckfragen from '../komponenten/UebergangeneRueckfragen'
import WareneingangFehlt from '../komponenten/WareneingangFehlt'
import { datum, heuteIso, plusTage, tagMonat } from '../format'
import { assistentPfad } from '../pfade'
import type { Faellig, FaelligArt, FaelligEintrag, ReportStand, Uebersicht, UebersichtImport } from '../typen'

/** Fünf Wochen: die Spanne einer Urlaubsvertretung und der Blick, den die Disposition braucht. */
const STANDARD_WOCHEN = 5

/** Älter als drei Tage: die Kladde hinkt, die Reichweiten stimmen nicht mehr. */
const KLADDE_ALT_AB_TAGEN = 3

const ARTEN: { wert: FaelligArt | ''; titel: string }[] = [
  { wert: '', titel: 'alles' },
  { wert: 'mindestbestand', titel: 'Unter Mindestbestand' },
  { wert: 'abruf', titel: 'Abrufe' },
  { wert: 'nachauflage', titel: 'Nachauflagen' },
  { wert: 'vorstufen', titel: 'Vorstufen' },
  { wert: 'lieferung', titel: 'Lieferungen' },
  { wert: 'frist', titel: 'Fristen' },
  { wert: 'reichweite', titel: 'Reichweitenenden' },
  { wert: 'vermerk', titel: 'Vermerke' },
  { wert: 'inventur', titel: 'Inventuren' },
  { wert: 'stammdaten', titel: 'Stammsätze bestätigen' },
  { wert: 'bestellabgleich', titel: 'Bestellungen klären' },
]

/** Sonntag dieser Woche als ISO-Datum - „diese Woche" heißt: bis zum Wochenende. */
function wochenende(heute: string): string {
  const tag = new Date(`${heute}T00:00:00`).getDay()
  return plusTage(heute, (7 - tag) % 7)
}

function scrollenZu(id: string) {
  window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 0)
}

/**
 * Die Seite „Heute" (unter /heute und /faellig): oben die Lage in Kacheln - was überfällig ist,
 * was diese Woche ansteht, Rückfragen, Importe in Freigabe, die offene Inventur und der Stand der
 * Kladde -, darunter die Terminliste über alle Blätter als eine Tabelle, Kalenderwoche für
 * Kalenderwoche. Überfälliges ist eingeklappt, die Liste beginnt bei heute. Für den Montagmorgen
 * und die Urlaubsübergabe.
 */
export default function FaelligSeite() {
  const heute = heuteIso()
  const [ab, setAb] = useState(heute)
  const [bis, setBis] = useState(plusTage(heute, STANDARD_WOCHEN * 7))
  const [art, setArt] = useState<FaelligArt | ''>('')
  const [ueberfaelligOffen, setUeberfaelligOffen] = useState(false)
  const [erledigtOffen, setErledigtOffen] = useState(false)
  const { daten, fehler, neuLaden } = useLaden<Faellig>(() => api.faellig(ab, bis), [ab, bis], {
    wenn: Boolean(ab && bis && bis >= ab),
  })
  // Die Kacheln kommen ohne KI aus zwei schnellen Abfragen; die Terminliste wartet nicht auf sie.
  const { daten: uebersicht, neuLaden: uebersichtLaden } = useLaden<Uebersicht>(() => api.uebersichtOffen(), [])
  const [stammdatenOffen, setStammdatenOffen] = useState(false)
  const { daten: reports, fehler: reportsFehler } = useLaden<ReportStand[]>(() => api.importeLetzte(), [])

  const alle = daten?.eintraege ?? []
  const eintraege = alle.filter((e) => !art || e.art === art)
  const ueberfaellig = eintraege.filter((e) => e.tag < heute)
  const kommend = eintraege.filter((e) => e.tag >= heute)
  const frage = daten
    ? `Was steht bis ${datum(daten.bis)} an und in welcher Reihenfolge gehe ich es an? Die Fällig-Liste sagt: ${daten.zusammenfassung} Bitte je Punkt kurz sagen, was zu tun ist und was fehlt.`
    : ''

  function ueberfaelligZeigen() {
    setUeberfaelligOffen(true)
    scrollenZu('ueberfaellig')
  }

  return (
    <section>
      <h2>Heute</h2>
      <p className="hinweis">
        Was bis zu einem Datum ansteht, über alle Blätter: Abrufe, Nachauflagen, Vorstufen, erwartete Lieferungen,
        Fristen, das Ende einer Reichweite, fällige Vermerke, offene Inventuren. Überfälliges steht mit dabei.
      </p>

      <Statuszeile
        alle={alle}
        heute={heute}
        uebersicht={uebersicht}
        reports={reports}
        reportsFehler={reportsFehler}
        onUeberfaellig={ueberfaelligZeigen}
        onDieseWoche={() => scrollenZu('termine')}
        onDatenpruefung={() => scrollenZu('datenpruefung')}
        onStammdaten={() => setStammdatenOffen((offen) => !offen)}
      />

      {stammdatenOffen && (
        <FehlendeArtikel
          onFertig={() => {
            setStammdatenOffen(false)
            uebersichtLaden()
          }}
        />
      )}

      <OffenesAusDemAssistenten uebersicht={uebersicht} onGeaendert={uebersichtLaden} />

      <SelectLineWarnungen uebersicht={uebersicht} />

      <WareneingangFehlt />

      <UebergangeneRueckfragen uebersicht={uebersicht} />

      {/* Erledigt-Vermerke ändern die Zahl in der Kachel oben - darum lädt die Übersicht mit. */}
      <Datenpruefung onGeaendert={uebersichtLaden} />

      <div className="leiste" id="termine">
        <label className="hinweis">
          von <input type="date" value={ab} onChange={(e) => setAb(e.target.value)} />
        </label>
        <label className="hinweis">
          bis <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
        </label>
        {[2, 5, 8].map((w) => {
          const ziel = plusTage(ab, w * 7)
          const aktiv = bis === ziel
          return (
            <button key={w} className={aktiv ? 'aktiv' : ''} aria-pressed={aktiv} onClick={() => setBis(ziel)}>
              {w} Wochen
            </button>
          )
        })}
        <select value={art} onChange={(e) => setArt(e.target.value as FaelligArt | '')}>
          {ARTEN.map((a) => (
            <option key={a.wert} value={a.wert}>
              {a.titel}
            </option>
          ))}
        </select>
        <a className="knopf" href={api.exportFaellig(ab, bis)} download>
          Excel
        </a>
        {daten && (
          <Link className="knopf" to={assistentPfad(frage)}>
            Mit dem Assistenten durchgehen
          </Link>
        )}
      </div>
      {fehler && <Meldung art="fehler" titel="Liste nicht geladen">{fehler}</Meldung>}
      {daten && <p className="hinweis">{daten.zusammenfassung}</p>}
      {daten && eintraege.length === 0 && <p className="hinweis">Nichts fällig in diesem Zeitraum.</p>}
      {daten && eintraege.length > 0 && (
        <div className="termine-rahmen">
          <table className="termine">
            <colgroup>
              <col className="sp-datum" />
              <col className="sp-was" />
              <col className="sp-material" />
              <col className="sp-blatt" />
              <col className="sp-hinweis" />
            </colgroup>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Was</th>
                <th>Material</th>
                <th>Blatt</th>
                <th>Hinweis</th>
              </tr>
            </thead>
            {ueberfaellig.length > 0 && (
              <tbody id="ueberfaellig">
                <tr className="gruppe ueberfaellig-kopf">
                  <td colSpan={5}>
                    <button className="leise" aria-expanded={ueberfaelligOffen} onClick={() => setUeberfaelligOffen((o) => !o)}>
                      Überfällig ({ueberfaellig.length}) - {ueberfaelligOffen ? 'ausblenden' : 'anzeigen'}
                    </button>
                    {!ueberfaelligOffen && (
                      <span className="hinweis">
                        {' '}
                        seit {datum(ueberfaellig[0].tag)}, ältestes zuerst - die Liste darunter beginnt bei heute.
                      </span>
                    )}
                  </td>
                </tr>
                {ueberfaelligOffen && <Wochenzeilen eintraege={ueberfaellig} heute={heute} onGeaendert={neuLaden} />}
              </tbody>
            )}
            {kommend.length > 0 && (
              <tbody>
                <Wochenzeilen eintraege={kommend} heute={heute} onGeaendert={neuLaden} />
              </tbody>
            )}
            {kommend.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={5} className="hinweis">
                    Ab heute nichts fällig bis {datum(bis)} - nur Überfälliges.
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
      )}
      {daten && daten.erledigt.length > 0 && (
        <div className="termine-rahmen">
          <button className="leise" aria-expanded={erledigtOffen} onClick={() => setErledigtOffen((o) => !o)}>
            Erledigt / zur Kenntnis genommen ({daten.erledigt.length}) - {erledigtOffen ? 'ausblenden' : 'anzeigen'}
          </button>
          {erledigtOffen && (
            <table className="termine">
              <thead>
                <tr>
                  <th>Was</th>
                  <th>Von wem</th>
                  <th>Grund</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {daten.erledigt.map((e) => (
                  <tr key={e.erledigung.id}>
                    <td>
                      <b>{e.titel}</b> {e.text}
                    </td>
                    <td className="hinweis">
                      {e.erledigung.erledigt_von ?? '?'}, {datum(e.erledigung.erledigt_am)}
                    </td>
                    <td>{e.erledigung.grund ?? ''}</td>
                    <td>
                      <ErledigungAufheben id={e.erledigung.id} onGeaendert={neuLaden} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  )
}

/** Eine Erledigung zurücknehmen - der Eintrag steht danach wieder oben in der Liste. */
function ErledigungAufheben({ id, onGeaendert }: { id: number; onGeaendert: () => void }) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  async function aufheben() {
    setLaeuft(true)
    setFehler(null)
    try {
      await api.erledigungenAufheben(id)
      onGeaendert()
    } catch (f) {
      setFehler(fehlertext(f))
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <>
      <button className="leise" disabled={laeuft} onClick={aufheben}>
        zurücknehmen
      </button>
      {fehler && <span className="fehler"> {fehler}</span>}
    </>
  )
}

/** Die Einträge einer Liste, je Kalenderwoche eine Gruppenzeile davor - in einer gemeinsamen Tabelle. */
function Wochenzeilen({
  eintraege,
  heute,
  onGeaendert,
}: {
  eintraege: FaelligEintrag[]
  heute: string
  onGeaendert: () => void
}) {
  const zeilen: ReactElement[] = []
  let letzteKw: string | null = null
  eintraege.forEach((e, i) => {
    const kw = e.kw ?? '?'
    if (kw !== letzteKw) {
      zeilen.push(
        <tr className="gruppe" key={`kw-${kw}-${i}`}>
          <td colSpan={5}>{kw}</td>
        </tr>,
      )
      letzteKw = kw
    }
    const spaet = e.tag < heute
    zeilen.push(
      <tr key={`${e.art}-${e.artikelnummer}-${e.tag}-${i}`}>
        <td className={spaet ? 'unter-mindestbestand' : ''}>
          {datum(e.tag)}
          {spaet && ' überfällig'}
        </td>
        <td>
          <b>{e.titel}</b>
        </td>
        <td>
          {e.artikelnummer && (
            <Link to={e.link}>
              {e.artikelnummer} {e.bezeichnung}
            </Link>
          )}
          {!e.artikelnummer && e.gruppe && <Link to={e.link}>{e.gruppe}</Link>}
          {!e.artikelnummer && !e.gruppe && <Link to={e.link}>öffnen</Link>}
        </td>
        <td className="hinweis">{e.gruppe ?? ''}</td>
        <td>
          {e.text}
          {e.bestellung_id != null && <StimmtSo id={e.bestellung_id} onGeaendert={onGeaendert} />}
          <Erledigen schluessel={e.schluessel} fingerabdruck={e.fingerabdruck} onGeaendert={onGeaendert} />
        </td>
      </tr>,
    )
  })
  return <>{zeilen}</>
}

/**
 * Einen Eintrag als erledigt bzw. zur Kenntnis genommen markieren (Runde 4, 02.11.2026, Göran: „Die Plattform
 * muss selber merken, was schon bearbeitet wurde"). Er kommt von selbst wieder, sobald sich sein Anlass ändert.
 */
function Erledigen({
  schluessel,
  fingerabdruck,
  onGeaendert,
}: {
  schluessel: string
  fingerabdruck: string
  onGeaendert: () => void
}) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  async function erledigen() {
    const grund = window.prompt('Erledigt bzw. zur Kenntnis genommen - optional kurz begründen:', '') ?? undefined
    setLaeuft(true)
    setFehler(null)
    try {
      await api.erledigen(schluessel, fingerabdruck, grund || undefined)
      onGeaendert()
    } catch (f) {
      setFehler(fehlertext(f))
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <>
      {' '}
      <button className="leise" disabled={laeuft} onClick={erledigen} title="Erledigt bzw. zur Kenntnis genommen - steht nicht mehr hier, bis sich der Anlass ändert">
        erledigt
      </button>
      {fehler && <span className="fehler"> {fehler}</span>}
    </>
  )
}

/**
 * Eine Frage des Bestellabgleichs mit „stimmt so" beantworten. Ändern (Menge, Status) geht über die
 * Disposition oder den Assistenten; der Vermerk hier gilt nur für den jetzigen Stand der Bestellung.
 */
function StimmtSo({ id, onGeaendert }: { id: number; onGeaendert: () => void }) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  async function markieren() {
    setLaeuft(true)
    setFehler(null)
    try {
      await api.bestellungGeprueft(id)
      onGeaendert()
    } catch (f) {
      setFehler(fehlertext(f))
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <>
      {' '}
      <button className="leise" disabled={laeuft} onClick={markieren} title="Die Bestellung stimmt so - nicht mehr fragen">
        stimmt so
      </button>
      {fehler && <span className="fehler"> {fehler}</span>}
    </>
  )
}

/**
 * Die Lage in Kacheln, jede ein Link: Zahl und Wort, keine Farbe als einziger Träger. Nur Kacheln
 * mit einer Zahl über null - außer dem Datenstand, der immer steht, damit man sieht, wie alt die
 * Reichweiten sind.
 */
function Statuszeile({
  alle,
  heute,
  uebersicht,
  reports,
  reportsFehler,
  onUeberfaellig,
  onDieseWoche,
  onDatenpruefung,
  onStammdaten,
}: {
  alle: FaelligEintrag[]
  heute: string
  uebersicht: Uebersicht | null
  reports: ReportStand[] | null
  reportsFehler: string | null
  onUeberfaellig: () => void
  onDieseWoche: () => void
  onDatenpruefung: () => void
  onStammdaten: () => void
}) {
  const sonntag = wochenende(heute)
  const anzahlUeberfaellig = alle.filter((e) => e.tag < heute).length
  const anzahlWoche = alle.filter((e) => e.tag >= heute && e.tag <= sonntag).length
  const rueckfragen = (uebersicht?.rueckfragen ?? []).filter((r) => r.status === 'offen' || r.status === 'beantwortet').length
  // Was aus SelectLine angehalten ist oder auf seine Tage wartet, steht in der eigenen Karte darunter.
  const ausSelectLine = (i: UebersichtImport) => i.quelle === 'selectline' && ((i.gruende ?? []).length > 0 || Boolean(i.wartet))
  const importe = (uebersicht?.importe ?? []).filter((i) => i.status === 'offen' && !ausSelectLine(i)).length
  const angehalten = (uebersicht?.importe ?? []).filter((i) => i.quelle === 'selectline' && (i.gruende ?? []).length > 0).length
  const inventur = (uebersicht?.inventuren ?? []).find((i) => i.status === 'offen')
  const ergebnismenge = reports?.find((r) => r.dokumenttyp === 'ergebnismenge')
  // Die Datenprüfung als Kachel: Zahl der Befunde, das Wort nennt die Schweren; warnend nur bei Fehlern.
  const pruefung = uebersicht?.datenpruefung
  const befunde = pruefung ? pruefung.fehler + pruefung.warnung + pruefung.hinweis : 0
  // Aus dem täglichen Abgleich mit SelectLine: dort im Bestand, hier ohne Stammsatz.
  const ohneStammsatz = uebersicht?.stammdaten_fehlend ?? []
  // Aus dem Assistenten: angeschlagene Wächter und liegen gebliebene Fäden - die Karten stehen darunter.
  const waechter = uebersicht?.waechter_ausgeloest?.length ?? 0
  const faeden = uebersicht?.offene_faeden?.length ?? 0
  // Maßgeblich ist, bis wann die Zahlen reichen, nicht wann jemand sie hochgeladen hat.
  const datenAlter = ergebnismenge?.datenstand_tage_her ?? ergebnismenge?.tage_her ?? null
  const kladdeAlt = reports !== null && (ergebnismenge === undefined || datenAlter === null || datenAlter > KLADDE_ALT_AB_TAGEN)

  return (
    <div className="statuszeile">
      {anzahlUeberfaellig > 0 && (
        <a className="kachel warnend" href="#ueberfaellig" onClick={(e) => { e.preventDefault(); onUeberfaellig() }}>
          <span className="kachel-zahl">{anzahlUeberfaellig}</span>
          <span className="kachel-wort">überfällig</span>
        </a>
      )}
      {anzahlWoche > 0 && (
        <a className="kachel" href="#termine" onClick={(e) => { e.preventDefault(); onDieseWoche() }}>
          <span className="kachel-zahl">{anzahlWoche}</span>
          <span className="kachel-wort">diese Woche</span>
        </a>
      )}
      {waechter > 0 && (
        <a className="kachel warnend" href="#waechter" onClick={(e) => { e.preventDefault(); scrollenZu('waechter') }}>
          <span className="kachel-zahl">{waechter}</span>
          <span className="kachel-wort">{waechter === 1 ? 'Wächter hat angeschlagen' : 'Wächter haben angeschlagen'}</span>
        </a>
      )}
      {faeden > 0 && (
        <a className="kachel" href="#offene-faeden" onClick={(e) => { e.preventDefault(); scrollenZu('offene-faeden') }}>
          <span className="kachel-zahl">{faeden}</span>
          <span className="kachel-wort">offen aus dem Assistenten</span>
        </a>
      )}
      {rueckfragen > 0 && (
        <Link className="kachel" to="/rueckfragen">
          <span className="kachel-zahl">{rueckfragen}</span>
          <span className="kachel-wort">{rueckfragen === 1 ? 'Rückfrage' : 'Rückfragen'}</span>
        </Link>
      )}
      {angehalten > 0 && (
        <a className="kachel warnend" href="#selectline-warnungen" onClick={(e) => { e.preventDefault(); scrollenZu('selectline-warnungen') }}>
          <span className="kachel-zahl">{angehalten}</span>
          <span className="kachel-wort">{angehalten === 1 ? 'Warnung aus SelectLine - besprechen' : 'Warnungen aus SelectLine - besprechen'}</span>
        </a>
      )}
      {importe > 0 && (
        <Link className="kachel" to="/importe">
          <span className="kachel-zahl">{importe}</span>
          <span className="kachel-wort">{importe === 1 ? 'Import wartet auf Freigabe' : 'Importe warten auf Freigabe'}</span>
        </Link>
      )}
      {pruefung && befunde > 0 && (
        <a className={`kachel ${pruefung.fehler > 0 ? 'warnend' : ''}`} href="#datenpruefung" onClick={(e) => { e.preventDefault(); onDatenpruefung() }}>
          <span className="kachel-zahl">{befunde}</span>
          <span className="kachel-wort">Datenprüfung: {zaehlerText(pruefung)}</span>
        </a>
      )}
      {ohneStammsatz.length > 0 && (
        <a
          className="kachel warnend"
          href="#stammdaten"
          title={ohneStammsatz.map((a) => `${a.artikelnummer} ${a.bezeichnung}`).join('\n')}
          onClick={(e) => { e.preventDefault(); onStammdaten() }}
        >
          <span className="kachel-zahl">{ohneStammsatz.length}</span>
          <span className="kachel-wort">
            Artikel aus SelectLine ohne Stammsatz - bestätigen
          </span>
        </a>
      )}
      {inventur && (
        <Link className="kachel" to="/inventur">
          <span className="kachel-zahl">
            {inventur.gezaehlt} von {inventur.materialien}
          </span>
          <span className="kachel-wort">gezählt - Inventur {tagMonat(inventur.stichtag)}</span>
        </Link>
      )}
      {reports !== null && (
        <Link className={`kachel ${kladdeAlt ? 'warnend' : ''}`} to="/importe" title="Die Kladde rechnet mit der zuletzt gebuchten Ergebnismenge - entscheidend ist, bis wann deren Zahlen reichen">
          <span className="kachel-zahl">
            {datenAlter === null || ergebnismenge === undefined
              ? 'keine'
              : datenAlter === 0
                ? 'heute'
                : `${datenAlter} Tage alt`}
          </span>
          <span className="kachel-wort">
            {ergebnismenge?.datenstand
              ? `Produktion gebucht bis ${datum(ergebnismenge.datenstand)}`
              : ergebnismenge?.gebucht_am
                ? `letzte Ergebnismenge ${datum(ergebnismenge.gebucht_am.slice(0, 10))}`
                : 'Ergebnismenge gebucht'}
            {kladdeAlt && ' - Kladde veraltet, PDF mailen oder hochladen'}
          </span>
        </Link>
      )}
      {reports === null && reportsFehler && (
        <Link className="kachel warnend" to="/importe">
          <span className="kachel-zahl">unbekannt</span>
          <span className="kachel-wort">Datenstand nicht geladen: {reportsFehler}</span>
        </Link>
      )}
    </div>
  )
}
